import React, { useState, useEffect, useCallback } from "react";
import {
  FaChevronDown,
  FaCheck,
  FaExclamationCircle,
  FaInfoCircle,
  FaUsers,
  FaCoins,
  FaCalculator,
  FaCog,
  FaMagic
} from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";
import AccountSelectField from "./Account/AccountSelectField";
import CreateAccountModal from "./Account/CreateAccountModal";


const Accounting = () => {
  const [categorizedRules, setCategorizedRules] = useState({});
  const [defaultAccounts, setDefaultAccounts] = useState({});
  const [expanded, setExpanded] = useState({
    chs: true,
    brut: true,
    noim: true,
    totaux: true,
    other: true,
    default: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [accountTypes, setAccountTypes] = useState([]);

  // États pour la modal de création de compte
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAccountData, setNewAccountData] = useState({
    code: "",
    name: "",
    account_type: "",
    reconcile: false,
  });
  const [pendingSelection, setPendingSelection] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rulesResponse, typesResponse] = await Promise.all([
        apiService.getElement("/accounting/codes"),
        apiService.getElement("/accounting/account_types"),
      ]);

      if (rulesResponse.data?.categorized_rules) {
        setCategorizedRules(rulesResponse.data.categorized_rules);
        console.log("Categorized Rules:", rulesResponse.data.categorized_rules);
      }
      
      if (rulesResponse.data?.default_accounts) {
        setDefaultAccounts(rulesResponse.data.default_accounts);
        console.log("Default Accounts:", rulesResponse.data.default_accounts);
      }
      
      if (typesResponse.data) {
        setAccountTypes(typesResponse.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      setMessage({ type: "error", text: "Erreur lors du chargement des données" });
    }
  };

  // Configuration des catégories
  const categoryConfig = {    
    chs: {
      title: "Charges salariales",
      icon: <FaUsers className="text-red-600" />,
      color: "red",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-900",
      description: "Règles salariales pour les charges salariales"
    },
    brut: {
      title: "Bruts",
      icon: <FaCoins className="text-blue-600" />,
      color: "blue",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-900",
      description: "Règles salariales pour les éléments bruts"
    },
    noim: {
      title: "Non Imposable",
      icon: <FaCalculator className="text-yellow-600" />,
      color: "yellow",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      textColor: "text-yellow-900",
      description: "Règles salariales pour les éléments non imposables"
    },
    totaux: {
      title: "Nets",
      icon: <FaCalculator className="text-green-600" />,
      color: "green",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      textColor: "text-green-900",
      description: "Règles salariales pour les totaux"
    },
    default: {
      title: "Codes par défaut des éléments de rémunération",
      icon: <FaMagic className="text-purple-600" />,
      color: "purple",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-900",
      description: "Codes par défaut utilisés pour les déductions, primes, avantages, notes de frais."
    },
  };

  // Fonction pour rechercher les comptes comptables
  const loadAccountOptions = async (inputValue) => {
    if (inputValue.length < 2) return [];

    try {
      const response = await apiService.getElement(`/accounting/accounts/search?q=${inputValue}`);
      const options = response.data.map((account) => ({
        value: account.code,
        label: `${account.code} - ${account.name}`,
        account: account,
      }));

      if (options.length === 0 && /^[0-9]+$/.test(inputValue)) {
        options.push({
          value: inputValue,
          label: `${inputValue} - Créer ce nouveau compte`,
          isCreateOption: true,
        });
      }

      return options;
    } catch (error) {
      console.error("Erreur lors de la recherche des comptes:", error);
      return [];
    }
  };

  const handleSelectChange = (ruleId, field, selectedOption, category) => {
    if (selectedOption && selectedOption.isCreateOption) {
      setNewAccountData({
        code: selectedOption.value,
        name: "",
        account_type: "",
        reconcile: false,
      });
      setPendingSelection({ ruleId, field, code: selectedOption.value, category });
      setShowCreateModal(true);
    } else {
      const value = selectedOption ? selectedOption.value : "";
      setCategorizedRules(prev => ({
        ...prev,
        [category]: prev[category].map(rule =>
          rule.id === ruleId ? { ...rule, [field]: value } : rule
        )
      }));
      // Si on modifie un code par défaut, on met aussi à jour defaultAccounts pour l'envoi
      if (category === "default") {
        const key = ruleId.replace("default_", ""); // ex: default_deduction -> deduction
        setDefaultAccounts(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            [field === "account_debit" ? "debit" : "credit"]: value
          }
        }));
      }
    }
  };

  const CategorySection = ({ category, rules: categoryRules }) => {
    const config = categoryConfig[category];
    if (!config || !categoryRules || categoryRules.length === 0) return null;

    return (
      <div className="space-y-4">
        <div
          className={`flex items-center justify-between p-4 ${config.bgColor} ${config.borderColor} border rounded-lg cursor-pointer hover:shadow-md transition-all duration-200`}
          onClick={() =>
            setExpanded((prev) => ({ ...prev, [category]: !prev[category] }))
          }
        >
          <div className="flex items-center gap-3">
            {/* <div className="p-2 rounded-full bg-white shadow-sm">
              {config.icon}
            </div> */}
            <div>
              <h5 className={`font-semibold ${config.textColor}`}>
                {config.title} ({categoryRules.length})
              </h5>
              <p className={`text-sm ${config.textColor} opacity-75`}>
                {config.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`${config.textColor} opacity-60 transition-transform duration-200`} 
                 style={{ transform: expanded[category] ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
              <FaChevronDown />
            </div>
          </div>
        </div>
        {expanded[category] && (
          <div className="pl-4 space-y-4">
            {/* Affichage des codes par défaut */}

            {/* Liste des règles */}
            <div className="space-y-4">
              {categoryRules.map((rule) => (
                <div
                  key={rule.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all duration-200 bg-white"
                >
                  <div className="mb-4">
                    <h6 className="font-semibold text-gray-900 text-lg mb-2">
                      {rule.name}
                    </h6>
                    {/* <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0C5B6D] text-white">
                        {rule.code}
                      </span>
                      {rule.financial_display && (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
                          {rule.financial_display}
                        </span>
                      )}
                    </div> */}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AccountSelectField
                      label="Compte de débit"
                      value={rule.account_debit}
                      onChange={(selectedOption) =>
                        handleSelectChange(rule.id, "account_debit", selectedOption, category)
                      }
                      placeholder={`Rechercher ou utiliser: ${rule.default_debit || 'Aucun défaut'}`}
                      helpText="Compte où sera enregistrée la charge"
                      loadAccountOptions={loadAccountOptions}
                    />

                    <AccountSelectField
                      label="Compte de crédit"
                      value={rule.account_credit}
                      onChange={(selectedOption) =>
                        handleSelectChange(rule.id, "account_credit", selectedOption, category)
                      }
                      placeholder={`Rechercher ou utiliser: ${rule.default_credit || 'Aucun défaut'}`}
                      helpText="Compte de dette ou de trésorerie"
                      loadAccountOptions={loadAccountOptions}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Aplatir toutes les règles sauf la catégorie default
      const allRules = Object.entries(categorizedRules)
        .filter(([cat]) => cat !== "default")
        .map(([, rules]) => rules)
        .flat();

      const rulesPayload = allRules.map(rule => ({
        id: rule.id,
        account_debit: rule.account_debit || "",
        account_credit: rule.account_credit || "",
      }));

      // Envoi avec default_accounts
      await apiService.saveElement("/accounting/codes", {
        rules: rulesPayload,
        default_accounts: defaultAccounts,
      });

      setMessage({ type: "success", text: "Codes comptables enregistrés avec succès !" });
    } catch {
      setMessage({ type: "error", text: "Erreur lors de l'enregistrement." });
    } finally {
      setLoading(false);
    }
  };

  // Fonctions de gestion des inputs avec useCallback pour éviter les re-renders
  const handleCodeChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, code: e.target.value }));
  }, []);

  const handleNameChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleTypeChange = useCallback((selectedOption) => {
    setNewAccountData(prev => ({
      ...prev,
      account_type: selectedOption?.value || "",
    }));
  }, []);

  const handleReconcileChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, reconcile: e.target.checked }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    setNewAccountData({
      code: "",
      name: "",
      account_type: "",
      reconcile: false,
    });
    setPendingSelection(null);
  }, []);

  const handleCreateAccount = async () => {
    if (
      !newAccountData.code ||
      !newAccountData.name 
    ) {
      setMessage({ type: "error", text: "Tous les champs sont obligatoires" });
      return;
    }

    setCreateLoading(true);
    try {
      const response = await apiService.saveElement(
        "/accounting/accounts/create",
        newAccountData
      );

      if (response.data.success) {
        // Mettre à jour la règle avec le nouveau compte
        if (pendingSelection) {
          setCategorizedRules(prev => ({
            ...prev,
            [pendingSelection.category]: prev[pendingSelection.category].map(rule =>
              rule.id === pendingSelection.ruleId
                ? { ...rule, [pendingSelection.field]: newAccountData.code }
                : rule
            )
          }));
        }

        setMessage({ type: "success", text: "Compte créé avec succès !" });
        handleCloseModal();
      }
    } catch (error) {
      setMessage({ type: "error", text: "Erreur lors de la création du compte" });
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Information générale */}
        <div className="border border-[#0C5B6D]/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#0C5B6D] rounded-full">
              <FaInfoCircle className="text-white" size={20} />
            </div>
            <div>
              <h6 className="font-semibold text-[#0C5B6D] mb-2 text-lg">
                Cette configuration est utilisée pour l'enregistrement comptable des règles salariales.
                Elle est obligatoires pour exporter les données vers la comptabilité.
              </h6>
            </div>
          </div>
        </div>

        {/* Sections par catégorie */}
        <div className="space-y-6">
          {/* Affiche toutes les catégories sauf 'default' */}
          {Object.entries(categorizedRules)
            .filter(([category]) => category !== "default")
            .map(([category, categoryRules]) => (
              <CategorySection
                key={category}
                category={category}
                rules={categoryRules}
              />
            ))}
          {/* Affiche la catégorie 'default' en dernier si elle existe */}
          {categorizedRules.default && (
            <CategorySection
              key="default"
              category="default"
              rules={categorizedRules.default}
            />
          )}
          
          {Object.keys(categorizedRules).length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FaExclamationCircle className="mx-auto mb-3" size={24} />
              <p className="text-lg font-medium mb-1">
                Aucune règle salariale trouvée
              </p>
            </div>
          )}
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`flex items-center gap-3 p-4 rounded-lg border-l-4 ${
              message.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : "bg-red-50 border-red-500 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <FaCheck className="text-green-600" size={16} />
            ) : (
              <FaExclamationCircle className="text-red-600" size={16} />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Bouton d'enregistrement */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-8 py-3 bg-[#0C5B6D] text-white rounded-lg hover:bg-[#083f4c] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Enregistrement en cours...
              </>
            ) : (
              <>
                <FaCheck size={16} />
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de création de compte */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        newAccountData={newAccountData}
        onCodeChange={handleCodeChange}
        onNameChange={handleNameChange}
        onTypeChange={handleTypeChange}
        onReconcileChange={handleReconcileChange}
        onCreateAccount={handleCreateAccount}
        accountTypes={accountTypes}
        createLoading={createLoading}
      />
    </>
  );
};

export default Accounting;
